import { defineStore } from 'pinia';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

export const useWorkspaceStore = defineStore('workspace', {
  state: () => ({
    items: [],
    current: null,
    userId: null,
    initialized: false,
    loading: false,
  }),

  getters: {
    hasWorkspace: (state) => Boolean(state.current),
    canCreateWorkspace: (state) => state.items.some(
      (workspace) => workspace.permissions?.manage_workspace === true
    ),
  },

  actions: {
    async initialize(force = false) {
      const auth = useAuthStore();

      if (this.initialized && this.userId === auth.user?.id && !force) return;

      this.loading = true;

      try {
        const [listResponse, currentResponse] = await Promise.all([
          api.get('/api/v1/workspaces'),
          api.get('/api/v1/workspaces/current'),
        ]);

        this.items = listResponse.data.data;
        this.current = currentResponse.data.data;
        this.userId = auth.user?.id || null;
        this.initialized = true;
      } finally {
        this.loading = false;
      }
    },

    async create(name) {
      const response = await api.post('/api/v1/workspaces', { name });
      const workspace = response.data.data;
      const auth = useAuthStore();

      this.items = [
        ...this.items.filter((item) => item.id !== workspace.id),
        workspace,
      ].sort((left, right) => left.name.localeCompare(right.name));
      this.current = workspace;
      this.userId = auth.user?.id || null;
      this.initialized = true;

      if (auth.user) {
        auth.user.current_workspace_id = workspace.id;
      }

      return workspace;
    },

    async select(workspaceId) {
      const response = await api.put(
        '/api/v1/workspaces/' + workspaceId + '/select'
      );
      const auth = useAuthStore();

      this.current = response.data.data;

      if (auth.user) {
        auth.user.current_workspace_id = this.current.id;
      }

      return this.current;
    },

    async remove(workspaceId) {
      await api.delete('/api/v1/workspaces/' + workspaceId);
      this.items = this.items.filter((workspace) => workspace.id !== workspaceId);

      if (this.current?.id === workspaceId) {
        this.current = null;
        const fallback = this.items.find((workspace) => workspace.slug === 'platform')
          || this.items[0];
        if (fallback) await this.select(fallback.id);
      }
    },

    reset() {
      this.items = [];
      this.current = null;
      this.userId = null;
      this.initialized = false;
    },
  },
});
